import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import type { Booking, BookingWithVehicle, Vehicle } from "@/types";

export function useMyTrips() {
  const { user } = useAuth();
  const [trips, setTrips] = useState<BookingWithVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setTrips([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { data, error: bookingsError } = await supabase
        .from("bookings")
        .select("*")
        .eq("renter_id", user.id)
        .order("created_at", { ascending: false });
      if (bookingsError) throw bookingsError;

      const rows = (data as Booking[]) ?? [];
      const vehicleIds = Array.from(
        new Set(rows.map((row) => row.vehicle_id).filter((id): id is number => typeof id === "number"))
      );

      let vehicles: Vehicle[] = [];
      if (vehicleIds.length > 0) {
        const { data: vehicleData } = await supabase.from("vehicles").select("*").in("id", vehicleIds);
        vehicles = (vehicleData as Vehicle[]) ?? [];
      }
      const vehicleMap = new Map(vehicles.map((vehicle) => [vehicle.id, vehicle]));

      setTrips(
        rows.map((row) => ({
          ...row,
          vehicle: row.vehicle_id ? vehicleMap.get(row.vehicle_id) ?? null : null,
        }))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't load your trips right now.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  return { trips, loading, error, reload: load };
}